import React, { useState, useEffect, useCallback } from 'react';
import { getSellWithdrawals, approveSellWithdrawal, rejectSellWithdrawal } from '../../utils/api';
import Pagination from '../Pagination';
import CopyButton from '../CopyButton';

const fmt = (n, d = 2) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: '', label: 'All' },
];

const SellWithdrawals = ({ BC, fBC, vBC } = {}) => {
  // Withdrawal amounts are stored in USDT; show them in the base currency (BNB)
  // to match the user's Sell & Claim screen. Falls back to USDT if helpers absent.
  const money = (usdt, d = 4) => (vBC ? vBC(usdt, d) : `${fmt(usdt, d)} USDT`);
  const moneyPlain = (usdt, d = 2) => (fBC ? `${fBC(usdt, d)} ${BC || 'BNB'}` : `${fmt(usdt, d)} USDT`);
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [processingId, setProcessingId] = useState(null);

  // Inline reject UI
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await getSellWithdrawals(statusFilter, page);
      setRequests(res.requests || []);
      setCounts(res.counts || { pending: 0, approved: 0, rejected: 0 });
      setPages(res.pagination?.pages || 1);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load withdrawals');
    }
    setLoading(false);
  }, [statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id, roboId, amount) => {
    if (!window.confirm(`Approve withdrawal of ${moneyPlain(amount, 2)} for ${roboId}? This sends an on-chain payout from the main wallet.`)) return;
    setProcessingId(id); setMsg(''); setError('');
    try {
      const r = await approveSellWithdrawal(id);
      setMsg(r.message || 'Approved');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Approve failed');
    }
    setProcessingId(null);
  };

  const handleReject = async (id) => {
    if (!rejectReason.trim()) { setError('Rejection reason required'); return; }
    setProcessingId(id); setMsg(''); setError('');
    try {
      const r = await rejectSellWithdrawal(id, rejectReason.trim());
      setMsg(r.message || 'Rejected');
      setRejectingId(null); setRejectReason('');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Reject failed');
    }
    setProcessingId(null);
  };

  const statusBadge = (s) => {
    const map = { pending: 'badge-pending', approved: 'badge-approved', rejected: 'badge-rejected' };
    return <span className={`d-status-badge ${map[s] || ''}`}>{s}</span>;
  };

  const countFor = (k) => (k === 'pending' ? counts.pending : k === 'approved' ? counts.approved : k === 'rejected' ? counts.rejected : null);

  return (
    <div className="fade-in">
      <div className="card">
        {/* Header + tabs (inline layout so it doesn't depend on missing CSS) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>Sell Withdrawals</h3>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {TABS.map(t => {
              const active = statusFilter === t.key;
              const c = countFor(t.key);
              return (
                <button
                  key={t.key || 'all'}
                  className={`btn btn-sm ${active ? 'btn-primary' : ''}`}
                  onClick={() => { setStatusFilter(t.key); setPage(1); }}
                >
                  {t.label}{c ? ` (${c})` : ''}
                </button>
              );
            })}
          </div>
        </div>

        {msg && <div className="notice notice-success">{msg}</div>}
        {error && <div className="notice notice-error">{error}</div>}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Wallet</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-3">Loading...</td></tr>}
              {!loading && requests.length === 0 && <tr><td colSpan={6} className="text-3">No withdrawals.</td></tr>}
              {!loading && requests.map(w => (
                <React.Fragment key={w._id}>
                  <tr>
                    <td>{new Date(w.requestedAt).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</td>
                    <td className="mono">{w.roboId}</td>
                    <td className="mono text-3" title={w.walletAddress}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {w.walletAddress ? `${w.walletAddress.slice(0, 6)}...${w.walletAddress.slice(-4)}` : '-'}
                        {w.walletAddress && <CopyButton text={w.walletAddress} title="Copy wallet" size={12} />}
                      </span>
                    </td>
                    <td className="mono">{money(w.amount, 4)}</td>
                    <td>{statusBadge(w.status)}</td>
                    <td>
                      {w.status === 'pending' ? (
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          <button className="btn btn-sm btn-primary" disabled={processingId === w._id}
                            onClick={() => handleApprove(w._id, w.roboId, w.amount)}>
                            {processingId === w._id ? '...' : 'Approve'}
                          </button>
                          <button className="btn btn-sm btn-danger" disabled={processingId === w._id}
                            onClick={() => { setRejectingId(rejectingId === w._id ? null : w._id); setRejectReason(''); setError(''); }}>
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-3">
                          {w.status === 'approved' && w.txHash ? `tx: ${w.txHash.slice(0, 10)}...` : ''}
                          {w.status === 'rejected' ? (w.rejectionReason || 'rejected') : ''}
                        </span>
                      )}
                    </td>
                  </tr>
                  {rejectingId === w._id && (
                    <tr>
                      <td colSpan={6}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', padding: '0.5rem 0' }}>
                          <input
                            className="input"
                            style={{ flex: 1, minWidth: '200px' }}
                            placeholder="Reason for rejection"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                          />
                          <button className="btn btn-sm btn-danger" disabled={processingId === w._id}
                            onClick={() => handleReject(w._id)}>
                            Confirm Reject
                          </button>
                          <button className="btn btn-sm" onClick={() => { setRejectingId(null); setRejectReason(''); }}>
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} pages={pages} onPage={setPage} />
      </div>
    </div>
  );
};

export default SellWithdrawals;